import {
  CompanyOnboardingStatus,
  ModuleOnboardingStatus,
  OnboardingModuleKey,
  OnboardingStatusBase,
  ONBOARDING_MODULE_LABELS,
  UserOnboardingStatus,
  WorkerOnboarding,
} from '../types';

const MODULE_KEYS: OnboardingModuleKey[] = ['teams', 'pay', 'time'];

// The two shapes the API serves: per-module from the company worker endpoint,
// a roll-up from the /basic user endpoints, which splits per company in by_company
// and per module again inside each company.
export type AnyOnboarding = WorkerOnboarding | UserOnboardingStatus;

export interface OnboardingModuleEntry {
  key: OnboardingModuleKey;
  status: ModuleOnboardingStatus;
}

// What the UI actually renders — one progress row, module or roll-up alike.
export interface OnboardingRow {
  key: string;
  label: string;
  status: OnboardingStatusBase;
}

// One section of the onboarding card. pay-app looks at a single company, so its rows
// are ungrouped and `label` is absent; Legend is cross-company and gets one labelled,
// collapsible section per company, each holding that company's module rows.
export interface OnboardingGroup {
  key: string;
  label?: string;
  companyId?: string;
  // The company roll-up, shown on the section header
  status?: OnboardingStatusBase;
  rows: OnboardingRow[];
}

export const ONBOARDING_OVERALL_LABEL = 'Overall';

export type AggregateOnboarding =
  | { state: 'not_assessed' }
  | { state: 'ready' }
  | { state: 'incomplete'; percent: number };

const isWorkerOnboarding = (onboarding: AnyOnboarding): onboarding is WorkerOnboarding =>
  MODULE_KEYS.some(key => key in onboarding);

// Non-null modules in a stable order. A null module means "invisible to this caller
// or not evaluated" — it is excluded, never treated as 0%. The roll-up shape carries
// no per-module breakdown at all, so it yields no entries.
export const getOnboardingModuleEntries = (onboarding?: AnyOnboarding | null): OnboardingModuleEntry[] => {
  if (!onboarding || !isWorkerOnboarding(onboarding)) {
    return [];
  }
  return MODULE_KEYS.reduce<OnboardingModuleEntry[]>((entries, key) => {
    const status = onboarding[key];
    if (status) {
      entries.push({ key, status });
    }
    return entries;
  }, []);
};

export const getOnboardingModuleLabel = ({ key, status }: OnboardingModuleEntry): string =>
  status.module || ONBOARDING_MODULE_LABELS[key];

// is_complete: true renders as 100% even when missing_recommended is non-empty
export const getModuleCompletionPercent = (status: OnboardingStatusBase): number | undefined =>
  status.is_complete ? 100 : status.completion_percent;

const getModuleRows = (onboarding?: WorkerOnboarding | null): OnboardingRow[] =>
  getOnboardingModuleEntries(onboarding).map(entry => ({
    key: entry.key,
    label: getOnboardingModuleLabel(entry),
    status: entry.status,
  }));

// A roll-up with nothing computed yet yields no row, so the UI reads "not assessed"
// instead of 0%.
const isEvaluatedRollUp = (status: OnboardingStatusBase & { evaluated?: boolean }): boolean =>
  status.evaluated !== false && typeof getModuleCompletionPercent(status) === 'number';

const getRollUpRows = (status: OnboardingStatusBase & { evaluated?: boolean }): OnboardingRow[] =>
  isEvaluatedRollUp(status) ? [{ key: 'overall', label: ONBOARDING_OVERALL_LABEL, status }] : [];

// Rows to render for either shape: one per evaluated module, or a single "Overall"
// row for the user-level roll-up. Nothing computed yet gives no rows at all.
export const getOnboardingRows = (onboarding?: AnyOnboarding | null): OnboardingRow[] => {
  if (!onboarding) {
    return [];
  }
  return isWorkerOnboarding(onboarding) ? getModuleRows(onboarding) : getRollUpRows(onboarding);
};

export const getCompanyOnboardingLabel = (status: CompanyOnboardingStatus, companyId: string): string =>
  status.trading_name || status.company_name || companyId;

const getCompanyOnboarding = (
  onboarding: AnyOnboarding | null | undefined,
  companyId: string
): CompanyOnboardingStatus | undefined => {
  if (!onboarding || isWorkerOnboarding(onboarding)) {
    return undefined;
  }
  return onboarding.by_company?.[companyId] ?? undefined;
};

// Incomplete companies first — with a dozen of them the ones needing attention should
// not be buried — then alphabetically so the list stays predictable between renders.
const compareGroups = (a: OnboardingGroup, b: OnboardingGroup): number => {
  const completeness = Number(!!a.status?.is_complete) - Number(!!b.status?.is_complete);
  return completeness || (a.label ?? '').localeCompare(b.label ?? '');
};

// Card sections for either shape. The company worker endpoint gives one unlabelled
// group of module rows; the user roll-up gives one group per company, each with its
// own module rows, falling back to a single "Overall" row for a company the gateway
// sent without a breakdown.
export const getOnboardingGroups = (onboarding?: AnyOnboarding | null): OnboardingGroup[] => {
  if (!onboarding) {
    return [];
  }

  if (isWorkerOnboarding(onboarding)) {
    const rows = getModuleRows(onboarding);
    return rows.length ? [{ key: 'modules', rows }] : [];
  }

  const byCompany = onboarding.by_company;
  if (byCompany) {
    const groups = Object.keys(byCompany)
      .filter(companyId => !!byCompany[companyId])
      .map<OnboardingGroup>(companyId => {
        const status = byCompany[companyId];
        const moduleRows = getModuleRows(status.by_module);
        return {
          key: status.company_id || companyId,
          companyId: status.company_id || companyId,
          label: getCompanyOnboardingLabel(status, companyId),
          status,
          rows: moduleRows.length ? moduleRows : getRollUpRows(status),
        };
      })
      .filter(group => !!group.rows.length)
      .sort(compareGroups);

    if (groups.length) {
      return groups;
    }
  }

  const rows = getRollUpRows(onboarding);
  return rows.length ? [{ key: 'overall', status: onboarding, rows }] : [];
};

// Companies a Legend refresh can target. Empty means the roll-up carries no
// per-company breakdown, so there is nothing to recompute from this screen.
export const getOnboardingCompanyIds = (onboarding?: AnyOnboarding | null): string[] =>
  getOnboardingGroups(onboarding).reduce<string[]>((ids, group) => {
    if (group.companyId) {
      ids.push(group.companyId);
    }
    return ids;
  }, []);

// Modules to recompute: the ones we know are in play, scoped to a company when the
// breakdown names them. An empty list means "all three" to the recompute endpoint.
export const getOnboardingModuleKeys = (
  onboarding?: AnyOnboarding | null,
  companyId?: string
): OnboardingModuleKey[] => {
  const byModule = companyId ? getCompanyOnboarding(onboarding, companyId)?.by_module : undefined;
  return getOnboardingModuleEntries(byModule ?? onboarding).map(entry => entry.key);
};

// Row aggregate = the minimum completion across evaluated modules (matches the server roll-up).
// Unknown is not complete: with zero evaluated modules the aggregate is 'not_assessed'.
export const getOnboardingAggregate = (onboarding?: AnyOnboarding | null): AggregateOnboarding => {
  const entries = getOnboardingModuleEntries(onboarding);
  const percents = entries
    .map(entry => getModuleCompletionPercent(entry.status))
    .filter((percent): percent is number => typeof percent === 'number');

  if (!percents.length) {
    return { state: 'not_assessed' };
  }
  if (entries.every(entry => entry.status.is_complete)) {
    return { state: 'ready' };
  }
  return { state: 'incomplete', percent: Math.min(...percents) };
};

// Module names arrive in several spellings across the API surface: 'Payroll' / 'pay',
// 'Teams' / 'teams', 'Time&Attendance' / 'Time & Attendance' / 'time'. Normalise by
// stripping everything but letters so all of them resolve to one onboarding key.
const MODULE_NAME_TO_KEY: Record<string, OnboardingModuleKey> = {
  teams: 'teams',
  pay: 'pay',
  payroll: 'pay',
  time: 'time',
  timeattendance: 'time',
};

export const getOnboardingModuleKeyByName = (name?: string | null): OnboardingModuleKey | undefined => {
  if (!name) {
    return undefined;
  }
  return MODULE_NAME_TO_KEY[name.toLowerCase().replace(/[^a-z]/g, '')];
};
